import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  JobStatus,
  SyncScheduleResponse,
} from '@/features/cloud-storage/services/SharePointService';

/**
 * SharePoint Store
 *
 * Manages SharePoint upload state, progress tracking, and sync schedules.
 * Used by SharePoint upload components to track file migration status
 * and manage document synchronization.
 *
 * Features:
 * - Upload job tracking with progress and status
 * - Document processing counts
 * - SSE connection management
 * - Sync schedule management
 * - URL validation state
 *
 * Cleared by:
 * - Component unmount (cleanup method)
 * - Successful upload completion
 * - User cancellation
 */

export interface SharePointUploadState {
  // Current upload job
  currentJobId: string | null;
  uploadStatus: 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';
  uploadMessage: string;
  progress: number;
  processedDocs: number;
  totalDocs: number;

  // SharePoint URL
  sharePointUrl: string;
  isUrlValid: boolean;
  urlValidationMessage: string;

  // SSE/Polling state
  isConnected: boolean;
  connectionType: 'sse' | 'polling' | null;

  // Sync schedule
  syncSchedule: SyncScheduleResponse | null;
  syncIntervalDays: number;

  // UI state
  isModalOpen: boolean;
  currentStep: 1 | 2 | 3;
  isLoading: boolean;
  error: string | null;
}

interface SharePointActions {
  // URL management
  setSharePointUrl: (url: string) => void;
  validateUrl: (url: string) => boolean;

  // Upload management
  startUpload: (jobId: string) => void;
  updateUploadProgress: (status: JobStatus) => void;
  completeUpload: () => void;
  failUpload: (error: string) => void;
  cancelUpload: () => void;

  // Connection management
  setConnectionStatus: (
    connected: boolean,
    type: 'sse' | 'polling' | null
  ) => void;

  // Sync schedule management
  setSyncSchedule: (schedule: SyncScheduleResponse | null) => void;
  setSyncIntervalDays: (days: number) => void;

  // UI management
  setModalOpen: (open: boolean) => void;
  setCurrentStep: (step: 1 | 2 | 3) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Reset and cleanup
  reset: () => void;
  cleanup: () => void;
}

type SharePointStore = SharePointUploadState & SharePointActions;

const initialState: SharePointUploadState = {
  // Upload state
  currentJobId: null,
  uploadStatus: 'idle',
  uploadMessage: '',
  progress: 0,
  processedDocs: 0,
  totalDocs: 0,

  // URL state
  sharePointUrl: '',
  isUrlValid: false,
  urlValidationMessage: '',

  // Connection state
  isConnected: false,
  connectionType: null,

  // Sync state
  syncSchedule: null,
  syncIntervalDays: 1,

  // UI state
  isModalOpen: false,
  currentStep: 1,
  isLoading: false,
  error: null,
};

export const useSharePointStore = create<SharePointStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // URL management
      setSharePointUrl: (url: string) => {
        set({ sharePointUrl: url }, false, 'setSharePointUrl');
        // Auto-validate URL
        get().validateUrl(url);
      },

      validateUrl: (url: string) => {
        // Basic SharePoint URL validation
        const sharePointPattern = /^https:\/\/[\w.-]+\.sharepoint\.com\/.+$/i;
        const oneDrivePattern = /^https:\/\/[\w.-]+\.onedrive\.com\/.+$/i;
        const onedriveBusinessPattern =
          /^https:\/\/[\w.-]+-my\.sharepoint\.com\/.+$/i;

        const isValid =
          sharePointPattern.test(url) ||
          oneDrivePattern.test(url) ||
          onedriveBusinessPattern.test(url);

        const validationMessage = isValid
          ? ''
          : url.length > 0
            ? 'Please enter a valid SharePoint or OneDrive URL'
            : '';

        set(
          {
            isUrlValid: isValid,
            urlValidationMessage: validationMessage,
          },
          false,
          'validateUrl'
        );

        return isValid;
      },

      // Upload management
      startUpload: (jobId: string) => {
        set(
          {
            currentJobId: jobId,
            uploadStatus: 'uploading',
            uploadMessage: 'Starting upload...',
            progress: 0,
            processedDocs: 0,
            totalDocs: 0,
            error: null,
          },
          false,
          'startUpload'
        );
      },

      updateUploadProgress: (status: JobStatus) => {
        const {
          progress,
          message,
          processed_docs: processedDocs,
          total_docs: totalDocs,
          status: jobStatus,
        } = status;

        // Map job status to upload status
        let uploadStatus: SharePointUploadState['uploadStatus'] = 'processing';
        if (jobStatus === 'COMPLETED' || progress === 100) {
          uploadStatus = 'completed';
        } else if (jobStatus === 'FAILED') {
          uploadStatus = 'failed';
        } else if (jobStatus === 'PENDING') {
          uploadStatus = 'uploading';
        }

        set(
          {
            uploadStatus,
            uploadMessage: message,
            progress,
            processedDocs: processedDocs || get().processedDocs,
            totalDocs: totalDocs || get().totalDocs,
          },
          false,
          'updateUploadProgress'
        );
      },

      completeUpload: () => {
        set(
          {
            uploadStatus: 'completed',
            uploadMessage: 'Upload completed successfully!',
            progress: 100,
            currentStep: 3, // Move to sync step
            isLoading: false,
            error: null,
          },
          false,
          'completeUpload'
        );
      },

      failUpload: (error: string) => {
        set(
          {
            uploadStatus: 'failed',
            uploadMessage: `Upload failed: ${error}`,
            isLoading: false,
            error,
          },
          false,
          'failUpload'
        );
      },

      cancelUpload: () => {
        set(
          {
            currentJobId: null,
            uploadStatus: 'idle',
            uploadMessage: 'Upload cancelled',
            progress: 0,
            processedDocs: 0,
            totalDocs: 0,
            isLoading: false,
          },
          false,
          'cancelUpload'
        );
      },

      // Connection management
      setConnectionStatus: (
        connected: boolean,
        type: 'sse' | 'polling' | null
      ) => {
        set(
          {
            isConnected: connected,
            connectionType: type,
          },
          false,
          'setConnectionStatus'
        );
      },

      // Sync schedule management
      setSyncSchedule: (schedule: SyncScheduleResponse | null) => {
        set({ syncSchedule: schedule }, false, 'setSyncSchedule');
      },

      setSyncIntervalDays: (days: number) => {
        // Ensure days is between 1 and 365
        const validDays = Math.max(1, Math.min(365, days));
        set({ syncIntervalDays: validDays }, false, 'setSyncIntervalDays');
      },

      // UI management
      setModalOpen: (open: boolean) => {
        set({ isModalOpen: open }, false, 'setModalOpen');
        if (!open) {
          // Reset to first step when closing modal
          set({ currentStep: 1 }, false, 'resetStep');
        }
      },

      setCurrentStep: (step: 1 | 2 | 3) => {
        set({ currentStep: step }, false, 'setCurrentStep');
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading }, false, 'setLoading');
      },

      setError: (error: string | null) => {
        set({ error }, false, 'setError');
      },

      // Reset and cleanup
      reset: () => {
        set(initialState, false, 'reset');
      },

      cleanup: () => {
        // Clean up connections and reset state
        set(
          {
            ...initialState,
            syncSchedule: get().syncSchedule, // Preserve sync schedule
          },
          false,
          'cleanup'
        );
      },
    }),
    {
      name: 'sharepoint-storage',
    }
  )
);
